import { IcoClose } from "assets/icons";
import { AsideOverlay, Button, Label, TextArea } from "components";
import { BackofficeConfirmCancelModal } from "components/BackofficeCancelConfirmModal";
import RadioInput from "components/RadioInput";
import { ErrorHelper } from "helpers";
import { Application } from "models/Application";
import { ApplicationEventType, CancellationReason } from "models/types";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { ApplicationService } from "services/applicationService";

interface DrawerProps {
	isOpen: boolean;
	application: Application;
	onClose: () => void;
}

type CancellationReasonOption = {
	label: string;
	value: CancellationReason;
};

const cancellationReasons: CancellationReasonOption[] = [
	{
		label: "Tipo de intervenção errada",
		value: CancellationReason.TIPO_ERRADO
	},
	{
		label: "Solicitação duplicada",
		value: CancellationReason.DUPLICADA
	},
	{
		label: "Falta de documentação",
		value: CancellationReason.FALTA_DOCUMENTACAO
	},
	{
		label: "Outro",
		value: CancellationReason.OUTRO
	}
];

export function BackofficeCancelApplicationDrawer({
	isOpen,
	application,
	onClose
}: Readonly<DrawerProps>) {
	const navigate = useNavigate();
	const applicationService = useMemo(ApplicationService.getInstance, []);

	const [description, setDescription] = useState("");
	const [descriptionTouched, setDescriptionTouched] = useState(false);
	const [reason, setReason] = useState<string | null>(null);
	const [isConfirmModalOpen, setConfirmModalOpen] = useState(false);
	const [isSubmitting, setIsSubmitting] = useState(false);

	const isFormInvalid = !reason || description.length < 1;

	const handleOpenConfirmModal = () => {
		setConfirmModalOpen(true);
	};

	const handleCloseConfirmModal = () => {
		setConfirmModalOpen(false);
	};

	const resetState = () => {
		setDescription("");
		setReason(null);
		setConfirmModalOpen(false);
		setIsSubmitting(false);
		onClose();
	};

	const handleCancelApplication = async () => {
		setIsSubmitting(true);
		try {
			await applicationService.registerEvents(application.id, [
				{
					event: {
						type: ApplicationEventType.SOLICITACAO_CANCELADA,
						description,
						cancellationReason: reason ?? undefined
					}
				}
			]);
			toast.success("Solicitação cancelada com sucesso");
			navigate("/backoffice");
		} catch (error) {
			ErrorHelper.showErrorMessage(error);
		} finally {
			resetState();
		}
	};

	return (
		<>
			<AsideOverlay isOpen={isOpen} onClickOutside={onClose} from="right">
				<header className="flex h-18 items-center justify-between border-b border-neutral-high-200 p-6">
					<h2 className="font-heading-semibold-tiny">Cancelar solicitação</h2>
					<Button kind="icon" hierarchy="ghost" onClick={onClose}>
						<IcoClose />
					</Button>
				</header>
				<div className="flex flex-1 flex-col justify-between overflow-y-auto p-6">
					<div className="flex flex-col gap-6">
						<p className="text-sm">
							Para prosseguir com o cancelamento da solicitação, selecione uma
							das alternativas e justifique em detalhes.
						</p>
						<div className="[&>fieldset>div]:gap-4 [&>fieldset>div]:flex-col [&>fieldset>span]:text-neutral-low-400 [&>fieldset>span]:pb-2 [&_label]:text-base">
							<RadioInput
								label="Selecione o motivo do cancelamento"
								name="cancellation_reason"
								options={cancellationReasons}
								value={reason ?? ""}
								onChange={(e) => setReason(e.target.value)}
							/>
						</div>
						<div className="flex flex-col [&>div>div]:justify-end">
							<Label isRequired>Justificativa para o solicitante</Label>
							<TextArea
								label="Justificativa para o solicitante"
								placeholder="Digitar"
								value={description}
								onChange={(e) => setDescription(e.target.value)}
								maxLength={500}
								showIncreasingCharCount
								onBlur={() => setDescriptionTouched(true)}
								touched={descriptionTouched}
							/>
						</div>
					</div>
					<footer className="mt-6 flex w-full flex-row justify-end gap-4 border-t border-neutral-high-200 pt-6">
						<Button hierarchy="secondary" onClick={onClose}>
							Voltar
						</Button>
						<Button
							hierarchy="primary"
							className="bg-feedback-negative-pure-500 border-feedback-negative-pure-500"
							onClick={handleOpenConfirmModal}
							disabled={isFormInvalid}
						>
							Cancelar solicitação
						</Button>
					</footer>
				</div>
			</AsideOverlay>
			<BackofficeConfirmCancelModal
				isOpen={isConfirmModalOpen}
				isLoading={isSubmitting}
				applicationProcessCode={application.applicationProcessCode}
				onClose={handleCloseConfirmModal}
				onConfirm={handleCancelApplication}
			/>
		</>
	);
}
